import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ErrorReportingUtils } from '@mm/plugins';
import UploadController from '../controllers/UploadController';
import commonHandler from '../errorHandlers/common-handler';
import { UploadImgModel } from '../model/UploadImgModel';
import { selectActiveCompanyId } from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import Actions from './actions';

const getUploadImageId = async (
  dispatch: Dispatch<StoreState>,
  image: UploadImgModel,
  companyId: number,
): Promise<number> => {
  const [id, _url] = await getUploadImageIdAndUrl(dispatch, image, companyId);
  return id;
};

const getUploadImageIdAndUrl = async (
  dispatch: Dispatch<StoreState>,
  image: UploadImgModel,
  companyId: number,
): Promise<[number, string]> => {
  dispatch({ type: Actions.START_UPLOAD_IMAGE });
  try {
    const response = await UploadController.uploadImage(image, companyId);
    dispatch({ type: Actions.FINISH_UPLOAD_IMAGE });
    return [response.id, response.urls.original];
  } catch (error) {
    ErrorReportingUtils.reportError('UploadController.uploadImage() Error', {
      extraData: {
        error,
      },
    });
    const handlerResult = await commonHandler(dispatch)(error);
    return [handlerResult, ''];
  }
};

type GetUploadLandingPageLogo = (
  dispatch: Dispatch<StoreState>,
  image: any,
  landingPageId: number,
) => Promise<string>;

export const getUploadLandingPageLogo: GetUploadLandingPageLogo = async (
  dispatch,
  image,
  landingPageId,
) => {
  dispatch({ type: Actions.START_UPLOAD_IMAGE });
  try {
    const newLogoUrl = await UploadController.uploadLandingPageLogo(
      image,
      landingPageId,
    );
    dispatch({ type: Actions.FINISH_UPLOAD_IMAGE });
    return newLogoUrl;
  } catch (error) {
    return commonHandler(dispatch)(error);
  }
};

const getUploadVideoIdAndUrl = async (
  dispatch: Dispatch<StoreState>,
  video,
  companyId: number,
) => {
  dispatch({ type: Actions.START_UPLOAD_VIDEO });
  try {
    const response = await UploadController.uploadVideo(video, companyId);
    dispatch({ type: Actions.FINISH_UPLOAD_VIDEO });
    return [response.id, response.urls.original];
  } catch (error) {
    const handlerResult = await commonHandler(dispatch)(error);
    return [handlerResult, ''];
  }
};

const getUploadVideoId = async (
  dispatch: Dispatch<StoreState>,
  video,
  companyId: number,
) => {
  const [id, _url] = await getUploadVideoIdAndUrl(dispatch, video, companyId);
  return id;
};

const getUploadAudioIdAndUrl = async (
  dispatch: Dispatch<StoreState>,
  audio,
  companyId: number,
) => {
  dispatch({ type: Actions.START_UPLOAD_AUDIO });
  try {
    const response = await UploadController.uploadAudio(audio, companyId);
    dispatch({ type: Actions.FINISH_UPLOAD_AUDIO });
    return [response.id, response.urls.original];
  } catch (error) {
    const handlerResult = await commonHandler(dispatch)(error);
    return [handlerResult, ''];
  }
};

const getUploadAudioId = async (
  dispatch: Dispatch<StoreState>,
  audio,
  companyId: number,
) => {
  const [id, _url] = await getUploadAudioIdAndUrl(dispatch, audio, companyId);
  return id;
};

const getUploadFileId = async (
  dispatch: Dispatch<StoreState>,
  file,
  companyId: number,
) => {
  dispatch({ type: Actions.START_UPLOAD_FILE });
  try {
    const response = await UploadController.uploadFile(file, companyId);
    dispatch({ type: Actions.START_UPLOAD_FILE });
    return response.id;
  } catch (error) {
    return commonHandler(dispatch)(error);
  }
};
const getUploadEmailAttachmentId = async (
  dispatch: Dispatch<StoreState>,
  file,
  companyId: number,
  throwErrors: boolean,
) => {
  try {
    const response = await UploadController.uploadEmailAttachment(
      file,
      companyId,
    );
    return response.id;
  } catch (error) {
    if (throwErrors) {
      throw error;
    }
    return commonHandler(dispatch)(error);
  }
};

const getCompanyId = (getStore: () => StoreState) =>
  selectActiveCompanyId(getStore());

export function uploadImage(image: UploadImgModel) {
  return (
    dispatch: Dispatch<StoreState>,
    getStore: () => StoreState,
  ): Promise<number> =>
    getUploadImageId(dispatch, image, getCompanyId(getStore));
}

export function uploadImageWithUrl(image: UploadImgModel) {
  return (
    dispatch: Dispatch<StoreState>,
    getStore: () => StoreState,
  ): Promise<[number, string]> =>
    getUploadImageIdAndUrl(dispatch, image, getCompanyId(getStore));
}

type UploadLandingPageLogo = (
  image: any,
  landingPageId: number,
) => GetUploadLandingPageLogo;

export const uploadLandingPageLogo: UploadLandingPageLogo =
  (image, landingPageId) => dispatch =>
    getUploadLandingPageLogo(dispatch, image, landingPageId);

export function uploadVideo(video: UploadImgModel) {
  return (dispatch: Dispatch<StoreState>, getStore: () => StoreState) =>
    getUploadVideoId(dispatch, video, getCompanyId(getStore));
}

export function uploadAudio(audio: UploadImgModel) {
  return (dispatch: Dispatch<StoreState>, getStore: () => StoreState) =>
    getUploadAudioId(dispatch, audio, getCompanyId(getStore));
}

export function uploadVideoWithUrl(video: UploadImgModel) {
  return (dispatch: Dispatch<StoreState>, getStore: () => StoreState) =>
    getUploadVideoIdAndUrl(dispatch, video, getCompanyId(getStore));
}

export function uploadFile(file: UploadImgModel) {
  return (dispatch: Dispatch<StoreState>, getStore: () => StoreState) =>
    getUploadFileId(dispatch, file, getCompanyId(getStore));
}

export function uploadEmailAttachment(
  file: UploadImgModel,
  throwErrors: boolean = false,
) {
  return (dispatch: Dispatch<StoreState>, getStore: () => StoreState) =>
    getUploadEmailAttachmentId(
      dispatch,
      file,
      getCompanyId(getStore),
      throwErrors,
    );
}

export const useUploadActions = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  return useMemo(
    () => ({
      uploadImage: (image: UploadImgModel) => dispatch(uploadImage(image)),
      uploadImageWithUrl: (image: UploadImgModel) =>
        dispatch(uploadImageWithUrl(image)),
      uploadEmailAttachment: (
        file: UploadImgModel,
        throwErrors: boolean = false,
      ) => dispatch(uploadEmailAttachment(file, throwErrors)),
    }),
    [dispatch],
  );
};
